<script setup>
// Date + time on one row. The date half is TapDatePicker (any date, typed or
// picked, locale-aware); the time half is the 24h hour/minute selects.
//
// Callers keep `date` as a plain "yyyy-MM-dd" string (useFixtureForm compares
// and joins it with the time; PlayingAreaSelect filters on it) while
// TapDatePicker speaks in ISO instants and reads its model with `new Date()`,
// which would take a bare "yyyy-MM-dd" as UTC midnight — the previous day for
// anyone west of Greenwich. So the conversion lives here, in the local zone
// both ways.
import {computed} from 'vue';
import {DateTime} from 'luxon';
import TapDatePicker from './TapDatePicker.vue';
import SimpleTimeSelect from './SimpleTimeSelect.vue';

const props = defineProps({
  dateError: {
    type: Boolean,
    default: false,
  },
  dateErrorMessages: {
    type: [Array, String],
    default: () => [],
  },
  timeError: {
    type: Boolean,
    default: false,
  },
  timeErrorMessages: {
    type: [Array, String],
    default: () => [],
  },
  dateLabel: {
    type: String,
    default: 'Date',
  },
  timeLabel: {
    type: String,
    default: 'Time',
  },
  // X214: one above the other at every width (a narrow column, the schedule dialog's When step).
  stacked: {
    type: Boolean,
    default: false,
  },
});

const date = defineModel('date');
const time = defineModel('time');

const dateIso = computed({
  // Local midnight as the same "Z" string TapDatePicker commits itself, so its
  // external-change watcher stays quiet while someone is typing.
  get: () => {
    if (!date.value) return null;
    const d = DateTime.fromISO(date.value);
    return d.isValid ? d.startOf('day').toJSDate().toISOString() : null;
  },
  set: (iso) => {
    if (!iso) {
      date.value = null;
      return;
    }
    const d = DateTime.fromISO(iso);
    date.value = d.isValid ? d.toISODate() : null;
  },
});
</script>

<template>
  <!-- Stacked on a phone. Side by side at 7/5 the hour select truncated to
       "1.." and the minutes showed nothing at all at 390px. -->
  <v-row>
    <v-col cols="12" :sm="stacked ? 12 : 7">
      <tap-date-picker
          v-model="dateIso"
          :label="dateLabel"
          :error="dateError"
          :error-messages="dateErrorMessages"
      />
    </v-col>
    <v-col cols="12" :sm="stacked ? 12 : 5">
      <simple-time-select
          v-model="time"
          :error="timeError"
          :error-messages="timeErrorMessages"
      />
    </v-col>
  </v-row>
</template>
